/**
 * <s-scroller> — previous/next buttons for a horizontal scroll-snap track.
 *
 * Required markup:
 *   [data-scroller-track] — the horizontally scrolling list.
 *   button[data-scroller-prev], button[data-scroller-next] — scroll by one visible page.
 * Buttons are disabled at the start/end of the track.
 */
class SScroller extends HTMLElement {
  #ac;
  #track = null;
  #prev = null;
  #next = null;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-scroller-track]');
    this.#prev = this.querySelector('[data-scroller-prev]');
    this.#next = this.querySelector('[data-scroller-next]');
    if (!this.#track) return;

    this.#prev?.addEventListener('click', () => this.#scrollByPage(-1), opts);
    this.#next?.addEventListener('click', () => this.#scrollByPage(1), opts);
    this.#track.addEventListener('scroll', this.#queueUpdate, { ...opts, passive: true });
    window.addEventListener('resize', this.#queueUpdate, opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #scrollByPage(step) {
    if (!this.#track) return;
    const direction = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollBy({
      left: step * direction * this.#track.clientWidth * 0.9,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #queueUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    if (!this.#track) return;
    const position = Math.abs(this.#track.scrollLeft);
    const max = this.#track.scrollWidth - this.#track.clientWidth;
    if (this.#prev) this.#prev.disabled = position <= 2;
    if (this.#next) this.#next.disabled = position >= max - 2;
  }
}

if (!customElements.get('s-scroller')) {
  customElements.define('s-scroller', SScroller);
}
